<template>
    <ShopLte>
        <template #header>
            <div class="content-header">
                <div class="container-fluid">
                    <div class="row mb-2">
                        <div class="col-sm-6">
                            <h1>Báo cáo cân đối công nợ</h1>
                        </div>
                        <div class="col-sm-6">
                            <ol class="breadcrumb float-sm-right">
                                <li class="breadcrumb-item"><a href="/shop">Dashboard</a></li>
                                <li class="breadcrumb-item"><a href="/shop/financial">Tài chính</a></li>
                                <li class="breadcrumb-item active">Báo cáo công nợ</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <div class="content">
            <div class="container-fluid">
                <!-- Thống kê tổng quan -->
                <div class="row">
                    <div class="col-lg-3 col-6">
                        <div class="small-box bg-success">
                            <div class="inner">
                                <h3>{{ formatMoney(overallStats.total_receivable) }}</h3>
                                <p>Tổng phải thu</p>
                            </div>
                            <div class="icon">
                                <i class="fas fa-arrow-up"></i>
                            </div>
                            <div class="small-box-footer">
                                {{ overallStats.receivable_count }} khoản
                            </div>
                        </div>
                    </div>
                    <div class="col-lg-3 col-6">
                        <div class="small-box bg-danger">
                            <div class="inner">
                                <h3>{{ formatMoney(overallStats.total_payable) }}</h3>
                                <p>Tổng phải trả</p>
                            </div>
                            <div class="icon">
                                <i class="fas fa-arrow-down"></i>
                            </div>
                            <div class="small-box-footer">
                                {{ overallStats.payable_count }} khoản
                            </div>
                        </div>
                    </div>
                    <div class="col-lg-3 col-6">
                        <div class="small-box" :class="overallStats.net_position >= 0 ? 'bg-info' : 'bg-warning'">
                            <div class="inner">
                                <h3>{{ formatMoney(Math.abs(overallStats.net_position)) }}</h3>
                                <p>{{ overallStats.net_position >= 0 ? 'Vị thế thuận lợi' : 'Vị thế bất lợi' }}</p>
                            </div>
                            <div class="icon">
                                <i :class="overallStats.net_position >= 0 ? 'fas fa-thumbs-up' : 'fas fa-thumbs-down'"></i>
                            </div>
                            <div class="small-box-footer">
                                Cân đối công nợ
                            </div>
                        </div>
                    </div>
                    <div class="col-lg-3 col-6">
                        <div class="small-box bg-secondary">
                            <div class="inner">
                                <h3>{{ overallStats.partner_count }}</h3>
                                <p>Đối tác có công nợ</p>
                            </div>
                            <div class="icon">
                                <i class="fas fa-users"></i>
                            </div>
                            <div class="small-box-footer">
                                Đang theo dõi
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Bộ lọc -->
                <div class="card">
                    <div class="card-header">
                        <h3 class="card-title">Bộ lọc</h3>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <div class="col-md-4">
                                <label>Tìm kiếm đối tác</label>
                                <input
                                    v-model="localFilters.search"
                                    type="text"
                                    class="form-control"
                                    placeholder="Tên đối tác..."
                                    @input="debounceSearch"
                                >
                            </div>
                            <div class="col-md-3">
                                <label>Loại đối tác</label>
                                <select v-model="localFilters.partner_type" class="form-control" @change="applyFilters">
                                    <option value="">Tất cả</option>
                                    <option value="App\Models\Partner">Nhà cung cấp</option>
                                    <option value="App\Models\Customer">Khách hàng</option>
                                </select>
                            </div>
                            <div class="col-md-5">
                                <label>&nbsp;</label>
                                <div class="d-block">
                                    <button @click="clearFilters" class="btn btn-secondary me-2">
                                        <i class="fas fa-times"></i> Xóa bộ lọc
                                    </button>
                                    <button @click="exportExcel" class="btn btn-success" :disabled="exporting">
                                        <i class="fas fa-file-excel" v-if="!exporting"></i>
                                        <i class="fas fa-spinner fa-spin" v-else></i>
                                        {{ exporting ? 'Đang xuất...' : 'Xuất Excel' }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Bảng chi tiết công nợ -->
                <div class="card">
                    <div class="card-header">
                        <h3 class="card-title">Chi tiết công nợ theo đối tác</h3>
                    </div>
                    <div class="card-body p-0">
                        <table class="table table-striped">
                            <thead>
                                <tr>
                                    <th>Đối tác</th>
                                    <th>Loại</th>
                                    <th>Liên hệ</th>
                                    <th class="text-right">Phải thu</th>
                                    <th class="text-right">Phải trả</th>
                                    <th class="text-right">Cân đối</th>
                                    <th class="text-center">Số khoản</th>
                                    <th class="text-center">Thao tác</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="item in debtSummary.data" :key="`${item.partner_type}-${item.partner_id}`">
                                    <td>
                                        <div class="d-flex align-items-center">
                                            <div class="me-3">
                                                <i class="fas fa-building text-primary" v-if="item.partner_type.includes('Partner')"></i>
                                                <i class="fas fa-user text-info" v-else></i>
                                            </div>
                                            <div>
                                                <strong>{{ item.partner_name || 'Không có tên' }}</strong>
                                                <br>
                                                <small class="text-muted">{{ item.partner_address }}</small>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span class="badge" :class="item.partner_type.includes('Partner') ? 'badge-primary' : 'badge-info'">
                                            {{ item.partner_type.includes('Partner') ? 'Nhà cung cấp' : 'Khách hàng' }}
                                        </span>
                                    </td>
                                    <td>
                                        <div v-if="item.partner_phone">
                                            <i class="fas fa-phone text-muted"></i>
                                            {{ item.partner_phone }}
                                        </div>
                                    </td>
                                    <td class="text-right">
                                        <span v-if="item.receivable > 0" class="text-success font-weight-bold">
                                            {{ formatMoney(item.receivable) }}
                                        </span>
                                        <span v-else class="text-muted">-</span>
                                    </td>
                                    <td class="text-right">
                                        <span v-if="item.payable > 0" class="text-danger font-weight-bold">
                                            {{ formatMoney(item.payable) }}
                                        </span>
                                        <span v-else class="text-muted">-</span>
                                    </td>
                                    <td class="text-right">
                                        <span
                                            :class="item.net_balance > 0 ? 'text-success font-weight-bold' : item.net_balance < 0 ? 'text-danger font-weight-bold' : 'text-muted'"
                                        >
                                            {{ item.net_balance === 0 ? '-' : formatMoney(Math.abs(item.net_balance)) }}
                                            <i
                                                v-if="item.net_balance !== 0"
                                                :class="item.net_balance > 0 ? 'fas fa-arrow-up ml-1' : 'fas fa-arrow-down ml-1'"
                                            ></i>
                                        </span>
                                    </td>
                                    <td class="text-center">
                                        <span class="badge badge-secondary">
                                            {{ item.debt_count }}
                                        </span>
                                    </td>
                                    <td class="text-center">
                                        <div class="btn-group">
                                            <button
                                                @click="viewPartnerDetail(item)"
                                                class="btn btn-sm btn-info"
                                                title="Xem chi tiết"
                                            >
                                                <i class="fas fa-eye"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <div v-if="debtSummary.data.length === 0" class="text-center py-4">
                            <p class="text-muted">Không có công nợ nào</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </ShopLte>
</template>

<script>
import { ref, reactive } from 'vue'
import { Link } from '@inertiajs/inertia-vue3'
import { Inertia } from '@inertiajs/inertia'
import ShopLte from '@/Layouts/ShopLte.vue'
import { debounce } from 'lodash'

export default {
    name: 'DebtReportIndex',
    components: {
        Link,
        ShopLte
    },
    props: {
        debtSummary: {
            type: Object,
            required: true
        },
        overallStats: {
            type: Object,
            required: true
        },
        filters: {
            type: Object,
            default: () => ({})
        }
    },
    setup(props) {
        const localFilters = reactive({
            search: props.filters.search || '',
            partner_type: props.filters.partner_type || '',
        })

        const exporting = ref(false)

        // Debounced search
        const debounceSearch = debounce(() => {
            applyFilters()
        }, 300)

        const applyFilters = () => {
            Inertia.get('/shop/financial/debt-reports', localFilters, {
                preserveState: true,
                replace: true,
            })
        }

        const clearFilters = () => {
            localFilters.search = ''
            localFilters.partner_type = ''
            applyFilters()
        }

        const viewPartnerDetail = (partner) => {
            const params = new URLSearchParams({
                partner_type: partner.partner_type,
                partner_id: partner.partner_id,
            })
            Inertia.visit(`/shop/financial/debt-reports/partner-detail?${params}`)
        }

        const exportExcel = async () => {
            exporting.value = true
            try {
                const response = await fetch('/shop/financial/debt-reports/export-excel', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                    },
                    body: JSON.stringify(localFilters),
                })

                if (response.ok) {
                    const blob = await response.blob()
                    const url = window.URL.createObjectURL(blob)
                    const link = document.createElement('a')
                    link.href = url
                    link.download = `debt-report-${new Date().toISOString().split('T')[0]}.xlsx`
                    link.click()
                    window.URL.revokeObjectURL(url)
                } else {
                    const data = await response.json()
                    alert(data.message || 'Có lỗi xảy ra khi xuất file')
                }
            } catch (error) {
                console.error('Export error:', error)
                alert('Có lỗi xảy ra khi xuất file')
            } finally {
                exporting.value = false
            }
        }

        const formatMoney = (amount) => {
            if (!amount && amount !== 0) return '0 đ'
            return new Intl.NumberFormat('vi-VN').format(amount) + ' đ'
        }

        return {
            localFilters,
            exporting,
            debounceSearch,
            applyFilters,
            clearFilters,
            viewPartnerDetail,
            exportExcel,
            formatMoney
        }
    }
}
</script>
